import {
  fetchJobChainsForExport,
  invalidateJobChainsList,
  useChainJobs,
  useJobChainFilters,
  useJobStatus,
  useJobTags,
  useJobTypes,
} from '@/api/Jobs';
import { useTranslate } from '@/translations';
import { AppSearch, JobChainData, JobTypeEnum } from '@/types';
import {
  CustomColumns,
  CustomTable,
  faEye,
  Icon,
  VehicleInformation,
} from '@/ui/components';
import { AppRoutes } from '@/routes';
import { StorageKeys } from '@/utils/constants';
import { timeConverter } from '@/utils/helpers';
import { Button, Space, Tag, Tooltip } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { JobPopup } from '../JobPopup';
import { jobSources } from '../constants';

const getJobChainVins = (record: JobChainData): string[] =>
  record.vins?.filter(Boolean) ?? [];

export const JobChainsDataGrid = () => {
  const { t } = useTranslate();

  const [showAdd, setShowAdd] = useState(false);
  const defaultAppSearch: AppSearch<JobChainData> = useMemo(
    () => ({
      name: undefined,
      page: 1,
      pageSize: 10,
      filters: [],
      sortField: 'createdAt',
      sortOrder: 'descend',
    }),
    [],
  );
  const [query, setQuery] = useState<AppSearch<JobChainData> | undefined>(
    undefined,
  );

  const { data: jobChainsData, isFetching: isJobsLoading } =
    useChainJobs(query);
  const { data: filterOptions, isFetching: filtersLoading } =
    useJobChainFilters();
  const { data: jobTypes, isFetching: isJobTypesLoading } = useJobTypes();
  const { data: jobTags, isFetching: isJobTagsLoading } = useJobTags();
  const { data: jobStatus, isFetching: isJobStatusLoading } = useJobStatus();

  const isLoading =
    isJobsLoading ||
    filtersLoading ||
    isJobTypesLoading ||
    isJobTagsLoading ||
    isJobStatusLoading;

  const getExportData = async () => {
    if (!query) return [];
    return fetchJobChainsForExport(query);
  };

  const columns: CustomColumns<JobChainData> = useMemo(
    () => [
      {
        dataIndex: 'jobChainId',
        title: 'Chain ID',
        render: (value) => (
          <Link to={`${AppRoutes.jobs}/${value}`}>{value}</Link>
        ),
        exportRender: (value) => value?.toString() ?? '',
        width: 110,
        fixed: 'left',
        sortable: true,
      },
      {
        dataIndex: 'vins',
        title: t('vin'),
        render: (_value, record) => {
          const vins = getJobChainVins(record);
          if (vins.length === 0) return '—';
          if (vins.length === 1) {
            return <VehicleInformation vin={vins[0]} showAsLabel />;
          }
          return (
            <Tooltip
              title={
                <div className="fs-10">
                  {vins.map((vin) => (
                    <div key={vin}>{vin}</div>
                  ))}
                </div>
              }
            >
              <Button type="link" icon={<Icon icon={faEye} />} />
            </Tooltip>
          );
        },
        exportRender: (_value, record) => getJobChainVins(record).join(', '),
        sortable: true,
        filter: true,
        width: 200,
        fixed: 'left',
      },
      {
        dataIndex: 'locationId',
        title: t('location'),
        renderType: 'select',
        render: (_, data) => data.location || '-',
        exportRender: (_, record) => record.location || '',
        filter: true,
        filterOptions: filterOptions?.locations,
        width: 180,
      },
      {
        dataIndex: 'jobChainType',
        title: 'Type',
        renderType: 'select',
        render: (_, data) => data.jobChainTypeDescription,
        exportRender: (_, record) => record.jobChainTypeDescription ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.types,
        width: 200,
        fixed: 'left',
      },
      {
        dataIndex: 'statusId',
        title: 'Status',
        renderType: 'select',
        render: (_, data) => data.status,
        exportRender: (_, record) => record.status ?? '',
        sortable: true,
        filter: true,
        filterOptions: jobStatus?.map((o) => ({
          value: o.jobStatusId,
          text: o.description || o.name,
        })),
        width: 150,
        align: 'center',
      },
      {
        dataIndex: 'customerId',
        title: 'Customer',
        renderType: 'select',
        render: (_, data) => data.customerName,
        exportRender: (_, record) => record.customerName ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.customers,
        width: 200,
      },
      {
        dataIndex: 'subCustomerId',
        title: 'Sub Customer',
        renderType: 'select',
        render: (_, data) => data.subCustomerName,
        exportRender: (_, record) => record.subCustomerName ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.subCustomers || [],
        width: 200,
      },
      {
        dataIndex: 'jobSource',
        title: 'Job Source',
        renderType: 'select',
        sortable: true,
        filter: true,
        filterOptions: jobSources.map((o) => ({
          value: o.label,
          text: o.label,
        })),
        width: 250,
      },
      {
        dataIndex: 'tags',
        title: 'Tags',
        renderType: 'select',
        render: (_, value) => (
          <Space wrap>
            {value.tags.map((o) => (
              <Tag key={o.id} className="py-1 m-0 text-wrap">
                {o.name}
              </Tag>
            ))}
          </Space>
        ),
        exportRender: (_, record) =>
          record.tags?.map((o) => o.name).join(', ') ?? '',
        sortable: true,
        filter: true,
        filterOptions: (jobTags || []).map((o) => ({
          value: o.id,
          text: o.name,
        })),
        width: 200,
      },
      {
        dataIndex: 'notes',
        title: t('notes'),
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        dataIndex: 'createdAt',
        title: 'Create Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
        exportRender: (value) => timeConverter(value) || '',
      },
      {
        dataIndex: 'createdBy',
        title: 'Created By',
        sortable: true,
        filter: true,
        width: 200,
        align: 'center',
        ellipsis: true,
      },
      {
        dataIndex: 'updatedAt',
        title: 'Update Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
        exportRender: (value) => timeConverter(value) || '',
      },
      {
        dataIndex: 'updatedBy',
        title: 'Updated By',
        sortable: true,
        filter: true,
        width: 200,
        align: 'center',
        ellipsis: true,
      },
      {
        dataIndex: 'completedAt',
        title: 'Complete Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
        exportRender: (value) => timeConverter(value) || '',
      },
      {
        dataIndex: 'completedBy',
        title: 'Completed By',
        sortable: true,
        filter: true,
        width: 200,
        align: 'center',
        ellipsis: true,
      },
    ],
    [filterOptions, jobStatus, jobTags, t],
  );

  const onAppSearchChange = useCallback(
    (search: AppSearch<JobChainData>) => setQuery(search),
    [],
  );

  const appSearchOptions = useMemo(
    () => ({
      storageKey: StorageKeys.SearchJobs,
      defaultSearch: defaultAppSearch,
      value: query,
      onChange: onAppSearchChange,
    }),
    [defaultAppSearch, onAppSearchChange, query],
  );

  return (
    <>
      <CustomTable
        dataSource={jobChainsData?.entities || []}
        columns={columns}
        rowKey="jobChainId"
        loading={isLoading}
        pagination={{
          total: jobChainsData?.totalCount || 0,
          pageSize: query?.pageSize,
          current: query?.page || 1,
        }}
        exportOptions={{ fileName: 'Job Chains', getExportData }}
        appSearchOptions={appSearchOptions}
        onAdd={() => setShowAdd(true)}
        scroll={{ x: '100%' }}
      />
      <JobPopup
        jobTypes={
          jobTypes?.map((o) => ({
            value: o.value as JobTypeEnum,
            text: o.text,
          })) || []
        }
        jobTags={jobTags?.filter((o) => !o.deleted) || []}
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={() => {
          void invalidateJobChainsList();
        }}
      />
    </>
  );
};
